import Bullet from 'components/Bullet';
import InterviewStatusChip from 'components/Chips/InterviewStatusChip';
import List from 'components/list';
import { BoldTd, Td } from 'components/list/Td';
import { Th } from 'components/list/Th';
import INTERVIEW_TYPE from 'enums/InterviewTypes';
import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import GetOrderValue from 'types/GetOrderValue';
import Interview from 'types/Interview';
import isInterviewActionRequired from 'utils/interviewActionRequired';
import { getLocaleDate, getLocaleTime } from 'utils/time';

const DetailsLink = styled(Link)`
  font-weight: 500;
  color: var(--primary-700);
`;

export const columnNames = [
  'date',
  'hour',
  'status',
  'menteeName',
  'type',
  'viewDetails',
] as const;

export type ColumnNames = typeof columnNames[number];
export type Columns = {
  [key in ColumnNames]: boolean;
};

export const sortableColumns = ['date', 'id'] as const;
export type OrderValue = GetOrderValue<typeof sortableColumns[number]>;
// const OrderTh = getOrderTh<typeof sortableColumns[number]>();

export type Variant = 'mentee' | 'mentor' | 'adminMentee' | 'adminMentor';

type Props = {
  interviews: Interview[] | undefined;
  variant: Variant;
  currentUserId?: number;
  activeColumns: Columns;
  getLink?: (id: number) => string;
  pages: number | undefined;
  page: number;
  setPage: (page: number) => void;
  loading?: boolean;
  order: OrderValue;
  setOrder: (order: OrderValue) => void;
};
const InterviewList: FC<Props> = ({
  interviews,
  variant,
  currentUserId,
  activeColumns,
  getLink,
  pages,
  page,
  setPage,
  loading,
  // order,
  // setOrder,
}) => {
  return (
    <List
      activeColumns={activeColumns}
      columnNumber={variant === 'mentor' || variant === 'adminMentor' ? 6 : 5}
      isEmpty={!!interviews && interviews.length === 0}
      emptyText="Vous n'avez aucun entretien passé ni prévu pour le moment."
      loading={loading}
      {...{ pages, page, setPage }}
    >
      <Th name="date">Date</Th>
      <Th name="hour">Heure</Th>
      <Th name="status">Statut de l'entretien</Th>
      <Th name="menteeName">Mentoré</Th>
      <Th name="type">Type d'entretien</Th>
      <Th name="viewDetails"></Th>

      {interviews?.map((interview, idx) => {
        const link: string = getLink
          ? getLink(interview.id!)
          : '' + interview.id;

        return (
          <React.Fragment key={idx}>
            <Td name="date">
              <Link to={link}>
                {interview.date && getLocaleDate(interview.date)}
              </Link>
            </Td>
            <Td name="hour">
              {interview.date && getLocaleTime(interview.date)}
            </Td>
            <Td name="status">
              {/* On utilise maintenant la prop currentUserId */}
              {isInterviewActionRequired(variant, interview, currentUserId) && (
                <Bullet style={{ marginRight: '.5rem' }} />
              )}
              <InterviewStatusChip
                interview={{
                  ...interview,
                  // LOGIQUE DE CORRECTION DU STATUT :
                  // Si le backend confirme que l'utilisateur est l'auteur (isAuthor) 
                  // et que l'entretien est en attente, on affiche un statut neutre "En attente".
                  status:
                    ((interview as any).isAuthor &&
                    (interview.status === 'WAITING_MENTOR' || interview.status === 'WAITING_MENTEE'))
                      ? ('WAITING_REVIEW' as any)
                      : interview.status,
                }}
                currentUserId={currentUserId}
                variant={
                  variant === 'adminMentee' || variant === 'adminMentor'
                    ? 'admin'
                    : variant
                }
              />
            </Td>
            <BoldTd name="menteeName">
              <Link to={link}>
                {interview.mentorship?.mentee &&
                  `${interview.mentorship.mentee.firstName} ${interview.mentorship.mentee.lastName}`}
              </Link>
            </BoldTd>
            <Td name="type">
              {interview?.type && INTERVIEW_TYPE[interview.type]}
            </Td>
            <Td name="viewDetails">
              <DetailsLink to={link}>Voir l'entretien</DetailsLink>
            </Td>
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default InterviewList;
